import type { FieldCondition, VisibilityConditions, OutputField } from "@/types";

/**
 * Evaluate a single visibility condition against a set of field values.
 */
export function evaluateCondition(
  condition: FieldCondition,
  values: Record<string, unknown>
): boolean {
  const fieldValue = values[condition.field_slug];
  const targetValue = condition.value;

  switch (condition.operator) {
    case "equals":
      return String(fieldValue) === String(targetValue);
    case "not_equals":
      return String(fieldValue) !== String(targetValue);
    case "greater_than":
      return Number(fieldValue) > Number(targetValue);
    case "greater_than_or_equals":
      return Number(fieldValue) >= Number(targetValue);
    case "less_than":
      return Number(fieldValue) < Number(targetValue);
    case "less_than_or_equals":
      return Number(fieldValue) <= Number(targetValue);
    case "contains":
      return String(fieldValue)
        .toLowerCase()
        .includes(String(targetValue).toLowerCase());
    case "not_contains":
      return !String(fieldValue)
        .toLowerCase()
        .includes(String(targetValue).toLowerCase());
    case "is_empty":
      return (
        fieldValue === undefined || fieldValue === null || fieldValue === ""
      );
    case "is_not_empty":
      return (
        fieldValue !== undefined && fieldValue !== null && fieldValue !== ""
      );
    case "in": {
      const inValues = String(targetValue)
        .split(",")
        .map((v) => v.trim());
      return inValues.includes(String(fieldValue));
    }
    case "not_in": {
      const notInValues = String(targetValue)
        .split(",")
        .map((v) => v.trim());
      return !notInValues.includes(String(fieldValue));
    }
    default:
      return true;
  }
}

/**
 * Evaluate a full set of visibility conditions (AND/OR logic).
 */
export function evaluateVisibilityConditions(
  conditions: VisibilityConditions | null | undefined,
  values: Record<string, unknown>
): boolean {
  if (!conditions || conditions.conditions.length === 0) {
    return true;
  }
  const { logic, conditions: condList } = conditions;
  if (logic === "AND") {
    return condList.every((c) => evaluateCondition(c, values));
  }
  return condList.some((c) => evaluateCondition(c, values));
}

/**
 * Check if an output field should be visible given the simulation inputs.
 * - "always": visible
 * - "never": hidden
 * - "conditional": evaluate visibility_conditions against inputs
 */
export function isOutputFieldVisible(
  field: OutputField,
  inputs: Record<string, unknown>
): boolean {
  switch (field.visibility_mode) {
    case "never":
      return false;
    case "always":
      return true;
    case "conditional":
      return evaluateVisibilityConditions(field.visibility_conditions, inputs);
    default:
      return true;
  }
}
